import { useTranslation } from "react-i18next"
import { toast } from "react-toastify"

import { request } from "shared/api/request"
import { ExportsAPIType, serializer } from "shared/api/serializer/serializer"
import {
  ATTENTION_TOAST_TIMEOUT,
  DEFAULT_TOAST_TIMEOUT
} from "shared/constants/app-constants"
import { repeatUntil } from "shared/lib/repeatUntil"
import { raiseToast } from "shared/lib/toast/raiseToast"
import { useEvent } from "shared/lib/useEvent"
import { AvaGaebPhase } from "shared/types/AvaGaebPhase"
import { FileExport } from "shared/types/Export"
import { ToastType } from "shared/types/Toast"

export function useProjectGaebExport(projectID: string, isShareID = false) {
  const { t } = useTranslation()

  const fetchProjectGaebData = useEvent(
    async (format: string): Promise<FileExport> => {
      const fetchUrl: string = isShareID
        ? `${
            import.meta.env.VITE_API_BASE
          }/project-list/share/projects/${projectID}/export/${format}`
        : `${
            import.meta.env.VITE_API_BASE
          }/project-list/projects/${projectID}/export/${format}`

      return request(fetchUrl, {
        headers: {
          Accept: "application/*"
        }
      }).then(response =>
        response.json().then(json => {
          return serializer.deserialize(ExportsAPIType, json)
        })
      )
    }
  )

  const handleExport = useEvent(
    async ({ selectedOption }: { selectedOption: AvaGaebPhase | null }) => {
      const loadingToast = raiseToast({
        type: ToastType.LOADING,
        message: t("ProjectExport.Loading"),
        autoClose: false
      })

      function handleSuccess() {
        toast.update(loadingToast, {
          autoClose: DEFAULT_TOAST_TIMEOUT,
          data: {
            type: ToastType.SUCCESS,
            message: t("ProjectExport.Success")
          }
        })
      }

      function handleFailure(uiMessage: string | null) {
        toast.update(loadingToast, {
          autoClose: DEFAULT_TOAST_TIMEOUT,
          data: {
            type: ToastType.ERROR,
            timeout: ATTENTION_TOAST_TIMEOUT,
            message: `${t("ProjectExport.Failure")} ${uiMessage}`
          }
        })
      }

      if (selectedOption === null) {
        throw new Error("No project selected")
      }

      await repeatUntil(
        () => fetchProjectGaebData(selectedOption),
        (result: FileExport) => result.isComplete,
        (result: FileExport) => {
          if (result.url) {
            const tempLink = document.createElement("a")

            tempLink.href = result.url
            tempLink.click()
            tempLink.remove()

            handleSuccess()
            return Promise.resolve()
          }
          return Promise.reject(new Error("No Download URL given."))
        },
        { initialInterval: 1000, timeout: null }
      ).promise.catch(handleFailure)
    }
  )

  return {
    exportProject: handleExport
  }
}
